import React, { useState, useCallback } from 'react';
import { Card, Table, Tag, Button, Space, Modal, Form, Input, Select, Slider, message, Alert, Empty, Grid } from 'antd';
import { TeamOutlined, UsergroupAddOutlined, ReloadOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { metaAdsApi } from '../../utils/api';
import ColumnsCardList from '../common/ColumnsCardList';

/**
 * Gestion des audiences Meta : audiences personnalisées et audiences similaires (lookalike).
 *
 * Parcours type :
 *  1. Créer une audience source à partir des clients du backoffice
 *  2. Attendre que Meta la constitue (quelques heures)
 *  3. Générer une audience similaire à partir de cette source
 *  4. La sélectionner dans le ciblage d'une campagne
 */

// Pays proposés pour la recherche d'audiences similaires
const PAYS_SUGGERES = [
  { code: 'CI', nom: "Côte d'Ivoire" },
  { code: 'SN', nom: 'Sénégal' },
  { code: 'ML', nom: 'Mali' },
  { code: 'BF', nom: 'Burkina Faso' },
  { code: 'GN', nom: 'Guinée' },
  { code: 'BJ', nom: 'Bénin' },
  { code: 'TG', nom: 'Togo' },
  { code: 'CM', nom: 'Cameroun' },
  { code: 'FR', nom: 'France' }
];

/**
 * @param {object[]} audiences   - Liste partagée par la page (hook useAudiencesMeta)
 * @param {boolean}  loading     - Chargement en cours
 * @param {function} onRafraichir - Recharge la liste partagée
 */
const MetaAdsAudiences = ({ audiences = [], loading = false, onRafraichir }) => {
  // Mobile (< md) : cartes à la place du tableau ; `=== false` évite un flash avant la mesure
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [creationEnCours, setCreationEnCours] = useState(false);
  const [modalSource, setModalSource] = useState(false);
  const [modalLookalike, setModalLookalike] = useState(false);
  const [formSource] = Form.useForm();
  const [formLookalike] = Form.useForm();

  // La liste vient désormais de la page : rafraîchir ici met aussi à jour le
  // ciblage de l'écran de revue, qui lit la même source.
  const chargerAudiences = useCallback(() => {
    if (onRafraichir) onRafraichir();
  }, [onRafraichir]);

  /** Crée une audience source à partir des clients du backoffice */
  const creerAudienceSource = async (valeurs) => {
    try {
      setCreationEnCours(true);
      const { data } = await metaAdsApi.createAudienceFromCustomers(valeurs);
      message.success(data.message);
      setModalSource(false);
      formSource.resetFields();
      chargerAudiences();
    } catch (error) {
      console.error('Erreur lors de la création de l\'audience:', error);
      message.error(error.response?.data?.message || 'Erreur lors de la création de l\'audience');
    } finally {
      setCreationEnCours(false);
    }
  };

  /** Crée une audience similaire à partir d'une audience source */
  const creerLookalike = async (valeurs) => {
    try {
      setCreationEnCours(true);
      const { data } = await metaAdsApi.createLookalikeAudience(valeurs);
      message.success(data.message);
      setModalLookalike(false);
      formLookalike.resetFields();
      chargerAudiences();
    } catch (error) {
      console.error('Erreur lors de la création de l\'audience similaire:', error);
      message.error(error.response?.data?.message || 'Erreur lors de la création de l\'audience similaire');
    } finally {
      setCreationEnCours(false);
    }
  };

  // Seules les audiences personnalisées peuvent servir de source à une audience similaire
  const audiencesSources = audiences.filter(a => a.subtype !== 'LOOKALIKE');

  const colonnes = [
    {
      title: 'Audience',
      dataIndex: 'name',
      key: 'name',
      render: (nom, audience) => (
        <div>
          <div className="font-medium">{nom}</div>
          <div className="text-xs text-gray-500">{audience.statut || '—'}</div>
        </div>
      )
    },
    {
      title: 'Type',
      dataIndex: 'subtype',
      key: 'subtype',
      render: (type) => (
        <Tag color={type === 'LOOKALIKE' ? 'purple' : 'blue'}>
          {type === 'LOOKALIKE' ? 'Similaire' : 'Personnalisée'}
        </Tag>
      )
    },
    {
      title: 'Taille estimée',
      dataIndex: 'taille',
      key: 'taille',
      render: (taille) => (taille ? taille.toLocaleString() : 'En cours de calcul')
    },
    {
      title: 'Utilisable',
      dataIndex: 'pretePourDiffusion',
      key: 'pretePourDiffusion',
      render: (prete) => (
        <Tag color={prete ? 'green' : 'orange'}>{prete ? 'Prête' : 'En construction'}</Tag>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <Alert
        type="info"
        showIcon
        message="Comment fonctionnent les audiences similaires"
        description={
          <span>
            Meta analyse vos clients existants et construit une population qui leur ressemble.
            Créez d'abord une <strong>audience source</strong> depuis vos clients, puis générez
            l'audience similaire. Un pourcentage faible (1 %) donne une audience très proche de vos
            clients mais réduite ; un pourcentage élevé (10 %) élargit la portée en diluant la ressemblance.
          </span>
        }
      />

      <Card
        title={
          <div className="flex items-center space-x-2">
            <TeamOutlined className="text-blue-600" />
            <span>Audiences Meta</span>
          </div>
        }
        className="shadow-sm"
        extra={
          <Space wrap>
            <Button icon={<UsergroupAddOutlined />} onClick={() => setModalSource(true)}>
              Créer depuis mes clients
            </Button>
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              disabled={audiencesSources.length === 0}
              onClick={() => setModalLookalike(true)}
            >
              Créer une audience similaire
            </Button>
            <Button icon={<ReloadOutlined />} onClick={chargerAudiences} loading={loading} />
          </Space>
        }
      >
        {isMobile ? (
          <ColumnsCardList
            columns={colonnes}
            dataSource={audiences}
            loading={loading}
            headerKeys={['name']}
            clientPageSize={10}
            emptyText="Aucune audience. Commencez par en créer une depuis vos clients."
          />
        ) : (
        <Table
          rowKey="id"
          loading={loading}
          dataSource={audiences}
          columns={colonnes}
          pagination={{ pageSize: 10 }}
          locale={{
            emptyText: (
              <Empty description="Aucune audience. Commencez par en créer une depuis vos clients." />
            )
          }}
        />
        )}
      </Card>

      {/* Création d'une audience source depuis les clients du backoffice */}
      <Modal
        title="Créer une audience depuis mes clients"
        open={modalSource}
        onCancel={() => setModalSource(false)}
        onOk={() => formSource.submit()}
        confirmLoading={creationEnCours}
        okText="Créer l'audience"
        cancelText="Annuler"
      >
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="Données personnelles"
          description="Les emails et téléphones sont hachés (SHA-256) avant d'être envoyés : Meta ne reçoit jamais vos données clients en clair. Les clients sur liste noire sont exclus."
        />

        <Form form={formSource} layout="vertical" onFinish={creerAudienceSource}>
          <Form.Item
            name="name"
            label="Nom de l'audience"
            rules={[{ required: true, message: 'Donnez un nom à cette audience' }]}
            extra="Meta exige au moins 100 clients pour construire une audience exploitable"
          >
            <Input placeholder="Ex: Mes clients 2026" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Création d'une audience similaire */}
      <Modal
        title="Créer une audience similaire"
        open={modalLookalike}
        onCancel={() => setModalLookalike(false)}
        onOk={() => formLookalike.submit()}
        confirmLoading={creationEnCours}
        okText="Créer"
        cancelText="Annuler"
      >
        <Form
          form={formLookalike}
          layout="vertical"
          onFinish={creerLookalike}
          initialValues={{ country: 'CI', ratio: 1 }}
        >
          <Form.Item
            name="sourceAudienceId"
            label="Audience source"
            rules={[{ required: true, message: 'Choisissez une audience source' }]}
            extra="Meta cherchera des personnes ressemblant à celles de cette audience"
          >
            <Select
              placeholder="Sélectionnez une audience"
              options={audiencesSources.map(a => ({
                value: a.id,
                label: `${a.name}${a.taille ? ` (${a.taille.toLocaleString()} personnes)` : ''}`,
                disabled: !a.pretePourDiffusion
              }))}
            />
          </Form.Item>

          <Form.Item
            name="country"
            label="Pays de recherche"
            rules={[{ required: true, message: 'Choisissez un pays' }]}
            extra="Meta cherchera les personnes similaires uniquement dans ce pays"
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={PAYS_SUGGERES.map(p => ({ value: p.code, label: `${p.nom} (${p.code})` }))}
            />
          </Form.Item>

          <Form.Item
            name="ratio"
            label="Proximité avec vos clients"
            extra="1 % = très proche de vos clients mais audience réduite · 10 % = portée large, ressemblance plus faible"
          >
            <Slider
              min={1}
              max={10}
              marks={{ 1: '1 %', 3: '3 %', 5: '5 %', 10: '10 %' }}
            />
          </Form.Item>

          <Form.Item name="name" label="Nom (optionnel)">
            <Input placeholder="Généré automatiquement si vide" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MetaAdsAudiences;
